/**
 * Who is going to an inspection.
 *
 * Two slots. Either can hold a member of staff or a crew, because a crew in the
 * area looks at a roof as often as an inspector drives out to it, and a crew is
 * a row in `crews` rather than in `profiles`. That is why a slot is a pair of
 * columns on `jobs` and not one — see the 20260813120000 migration.
 *
 * Jonathon, 2026-08-13: "it should be any of our inspectors and crews as well
 * to where we can just search by name."
 */

import { crewLabel } from "./crewName.js";
import type { Crew, Job, Profile, StaffRole } from "./types.js";

export type AssigneeKind = "staff" | "crew";

export interface Assignee {
  kind: AssigneeKind;
  id: string;
  name: string;
  /** What they do, shown under the name: "Inspector", "Crew", "Sales rep". */
  detail: string;
}

/** One slot's two columns, whichever of them is filled. */
export interface AssigneeSlot {
  profileId: string | null;
  crewId: string | null;
}

/**
 * Every `staff_role`, spelled the way a person reads it.
 *
 * `Record<StaffRole, string>` and not `Record<string, string>` on purpose: this
 * map was written from memory and had `coordinator`, `admin` and `office` in
 * it, none of which are roles, while the two real ones it was missing —
 * `project_coordinator` and `alt_rep` — fell through to the raw column value.
 * Jonathon's line under his name read "Sales rep · project_coordinator ·
 * Project manager · Inspector · alt_rep". Typed this way, adding a value to the
 * enum without a label here does not compile.
 */
const ROLE_LABEL: Record<StaffRole, string> = {
  sales_rep: "Sales rep",
  alt_rep: "Alt rep",
  project_manager: "Project manager",
  project_coordinator: "Project coordinator",
  inspector: "Inspector",
};

const describe = (profile: Profile): string => {
  const roles = Array.isArray(profile.roles) ? profile.roles : [];
  // A role the database has and this map does not still names the person
  // something rather than nothing — but the type above is what prevents it.
  const named = roles.map((r) => ROLE_LABEL[r] ?? r).filter(Boolean);
  return named.length > 0 ? named.join(" · ") : "Staff";
};

/**
 * Everyone an inspection can be given to, in the order they should be offered.
 *
 * Inspectors first, then the rest of the staff, then the crews. Anybody
 * inactive is left out — they cannot be sent anywhere — except that somebody
 * already on this job is always offered, or opening the box would silently
 * unassign them.
 */
export function assigneeOptions(
  people: readonly Profile[],
  crews: readonly Crew[],
  keep: readonly string[] = [],
): Assignee[] {
  const kept = new Set(keep.filter(Boolean));

  const staff = people
    .filter((p) => p.active !== false || kept.has(p.id))
    .map<Assignee>((p) => ({
      kind: "staff",
      id: p.id,
      name: p.full_name ?? "—",
      detail: describe(p),
    }));

  const isInspector = (p: Profile) =>
    Array.isArray(p.roles) && p.roles.includes("inspector");
  const inspectorIds = new Set(people.filter(isInspector).map((p) => p.id));

  const inspectorsFirst = [
    ...staff.filter((a) => inspectorIds.has(a.id)),
    ...staff.filter((a) => !inspectorIds.has(a.id)),
  ];

  const crewOptions = crews
    .filter((c) => c.active !== false || kept.has(c.id))
    .map<Assignee>((c) => ({
      kind: "crew",
      id: c.id,
      name: crewLabel(c) ?? c.name,
      detail: "Crew",
    }));

  return [...inspectorsFirst, ...crewOptions];
}

/**
 * Who an inspection can be booked with: the staff who hold the inspector
 * role, and the crews.
 *
 * Jonathon, 2026-09-28: "for inspectors i want it to only be me, samuel, and
 * carlos as the options" — and, asked whether crews go too, to keep them. So
 * the rest of the office is no longer offered. The three of them are whoever
 * holds `inspector` in `profiles.roles` (migration 20260928400000 set it), so
 * the list changes with a row, not a release.
 *
 * It is also meant to end Samuel not finding himself. He did not hold the
 * role, so he sorted below the inspectors, and the picker draws only its first
 * eight names: with enough of the office ahead of him he was not in the list
 * until somebody typed his name. Inspectors now come first and nobody else on
 * staff is in the way.
 *
 * Whoever is already on this job is still offered, whatever they are, or
 * opening the box would silently unassign them.
 */
export function inspectorOptions(
  people: readonly Profile[],
  crews: readonly Crew[],
  keep: readonly string[] = [],
): Assignee[] {
  const kept = new Set(keep.filter(Boolean));
  const isInspector = (p: Profile) =>
    p.active !== false &&
    Array.isArray(p.roles) &&
    p.roles.includes("inspector");
  return assigneeOptions(people, crews, keep).filter(
    (a) =>
      a.kind === "crew" ||
      kept.has(a.id) ||
      people.some((p) => p.id === a.id && isInspector(p)),
  );
}

/** Name search, the same shape as everywhere else: every word has to appear. */
export function assigneeMatches(assignee: Assignee, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = `${assignee.name} ${assignee.detail}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/** The two slots as they stand on a job. */
export function inspectionSlots(
  job: Pick<
    Job,
    | "inspector_id"
    | "inspector_crew_id"
    | "inspector_2_id"
    | "inspector_2_crew_id"
  >,
): [AssigneeSlot, AssigneeSlot] {
  return [
    { profileId: job.inspector_id, crewId: job.inspector_crew_id },
    { profileId: job.inspector_2_id, crewId: job.inspector_2_crew_id },
  ];
}

/** Whoever is in a slot, or null. */
export function assigneeInSlot(
  slot: AssigneeSlot,
  options: readonly Assignee[],
): Assignee | null {
  if (slot.profileId) {
    return (
      options.find((a) => a.kind === "staff" && a.id === slot.profileId) ?? null
    );
  }
  if (slot.crewId) {
    return (
      options.find((a) => a.kind === "crew" && a.id === slot.crewId) ?? null
    );
  }
  return null;
}

/**
 * The columns to write for a slot.
 *
 * Both are always written, and one of them is always null: putting a crew in a
 * slot that held a person has to clear the person, or the database refuses the
 * write and the rep sees a constraint name.
 */
export function assigneePatch(
  slot: 1 | 2,
  picked: Assignee | null,
): Record<string, string | null> {
  const profileColumn = slot === 1 ? "inspector_id" : "inspector_2_id";
  const crewColumn = slot === 1 ? "inspector_crew_id" : "inspector_2_crew_id";
  return {
    [profileColumn]: picked?.kind === "staff" ? picked.id : null,
    [crewColumn]: picked?.kind === "crew" ? picked.id : null,
  };
}

/** Everyone on this job's inspection, for a calendar entry or a summary line. */
export function inspectionAssignees(
  job: Parameters<typeof inspectionSlots>[0],
  options: readonly Assignee[],
): Assignee[] {
  return inspectionSlots(job)
    .map((slot) => assigneeInSlot(slot, options))
    .filter((a): a is Assignee => a !== null);
}
